import {
  asyncLocalStorage,
  createDateFormat,
  downloadBlob,
  generateApi,
  generatePayload,
  persistActiveParvandehDefaultRoute,
  shouldRedirectToProfileRequiredTabs,
  storeDispatcher,
  updateParvandehStatusDetail,
} from "@/shared/utils";
import fetchMiddleWare, { ApiHooks } from "@/shared/useCustomFetch";
import { has, pathOr } from "ramda";

import { useHooks } from "@/shared/hooks";
import fetchMiddlewareQuery from "@/shared/useCostumQuery";
import { MutationOptions, useQueryClient } from "@tanstack/vue-query";

interface PropsInterface {
  immediate?: boolean;
  createNewInstance?: boolean;
  code?: string;
  resource?: string;
  resource_id?: string;
  fileName?: string;
  parvandeh?: any;
  url?: string;
  downloadFileName?: any;
  shouldReload?: boolean | false;
  urlKeys?: Array<string>;
}
type Hook = PropsInterface & ApiHooks;

export const getDate = (props: Hook) => {
  const url = generateApi(["globalServices", "date"]);
  return fetchMiddleWare({
    afterFetch({ data }) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { configs, ...currentDate } = data;
      storeDispatcher("currentDate", currentDate);
    },
    ...props,
    url,
    method: "GET",
  });
};

export const getPersonProfilePreview = (props: Hook) => {
  const url = generateApi(
    ["globalServices", "personProfilePreview"],
    [props.code]
  );

  return fetchMiddleWare({
    ...props,
    url,
    method: "GET",
  });
};

export const getPersonProfilePreviewPdf = (props: Hook) => {
  const url = generateApi(["globalServices", "personProfilePreviewPdf"]);

  return fetchMiddleWare({
    beforeFetch(ctx) {
      return generatePayload(ctx, {
        captcha: "captcha",
        key: "key",
      });
    },
    afterFetch({ data }) {
      if (data) {
        downloadBlob(data);
      }
    },
    ...props,
    url,
    method: "PATCH",
    responseType: "blob",
  });
};

export const generateOtpCodeNew = (props: Hook) => {
  const url = generateApi(["globalServices", "generate_otp_code"]);
  return fetchMiddleWare({
    beforeFetch(ctx) {
      return generatePayload(ctx, {
        model_request: "mode",
        mobile: "mobile",
        email: "email",
      });
    },
    afterFetch({ data }) {
      return {
        data: {
          controlCode: pathOr(null, ["control_code"], data),
          expireAt: pathOr(null, ["expire_at"], data),
          expireSeconds: pathOr(0, ["expire_seconds"], data),
          mobile: pathOr("", ["mobile"], data),
          email: pathOr("", ["email"], data),
        },
      };
    },
    ...props,
    url,
    method: "POST",
  });
};
export const deleteReport = (props: Hook) => {
  const url = generateApi(["globalServices", "report_id"]);
  return fetchMiddleWare({
    ...props,
    url,
    method: "DELETE",
  });
};

export const changeUserMobile = (props: Hook) => {
  const url = generateApi(["profile", "userChangeMobile"]);

  return fetchMiddleWare({
    beforeFetch(ctx) {
      return generatePayload(ctx, {
        two_step_code: "code",
        control_code: "controlCode",
        mobile: "mobile",
      });
    },
    afterFetch({ data }) {
      return {
        data: {
          message: pathOr("", ["configs", "message"], data),
        },
      };
    },
    ...props,
    url,
    method: "PATCH",
  });
};

export const tusAttachment = (props: Hook) => {
  const url = generateApi(["globalServices", "tusAttachment"]);

  return fetchMiddleWare({
    async beforeFetch(ctx) {
      return generatePayload(ctx, {
        upload_id: "uploadId",
      });
    },
    afterFetch({ data }) {
      return {
        data: {
          ...data,
        },
      };
    },
    ...props,
    url,
    createNewInstance: true,
    method: "POST",
  });
};

export const changeUserPassword = (props: Hook) => {
  const url = generateApi(["profile", "userChangePass"]);

  return fetchMiddleWare({
    beforeFetch(ctx) {
      return generatePayload(ctx, {
        password: "password",
      });
    },
    afterFetch({ data }) {
      return {
        data: {
          message: pathOr("", ["configs", "message"], data),
        },
      };
    },
    ...props,
    url,
    method: "PATCH",
  });
};

export const getConvertVoiceToText = (props: Hook) => {
  const url = generateApi(["globalServices", "convertVoiveToText_code"]);

  return fetchMiddleWare({
    afterFetch({ data }) {
      try {
        const outPutText = JSON.parse(pathOr("", ["output_text"], data)).map(
          (item: any, index: any) => ({ ...item, id: index + 1 })
        );
        const outPutTextLength = outPutText.length - 1;
        return {
          data: {
            outPutText,
            outPutTextLength,
            createdAt: createDateFormat(
              pathOr("", ["created_at", "jalali"], data)
            ),
            updatedAt: createDateFormat(
              pathOr("", ["updated_at", "jalali"], data)
            ),
            algorithmName: pathOr(null, ["algorithm_name"], data),
            convertTime: pathOr(null, ["convert_time"], data),
            fileDuration: pathOr(null, ["file_duration"], data),
          },
        };
      } catch (e) {
        console.log(e);
      }
    },
    ...props,
    url,
    immediate: false,
    method: "GET",
  });
};

export function chatCompletions<
  TData = any,
  TError = any,
  TVariables extends Record<string, any> = Record<string, any>
>({
  options,
}: {
  props: PropsInterface;
  options?: MutationOptions<TData, TError, TVariables, unknown>;
}) {
  const url = generateApi(["globalServices", "chatCompletions"]);
  return fetchMiddlewareQuery({
    url,
    method: "post",
    mapping: {
      prompt_code: "promptCode",
      user_role_content: "userRoleContent",
    },
    transformResponse({ data }) {
      return {
        ...data,
      };
    },
    options: {
      ...options,
    },
  });
}

export const editConvertVoiceToText = (props: Hook) => {
  const url = generateApi(["globalServices", "convertVoiveToText_code"]);

  return fetchMiddleWare({
    beforeFetch(ctx) {
      return generatePayload(ctx, {
        output_text: "outPutText",
      });
    },
    ...props,

    url,
    immediate: false,
    method: "PATCH",
  });
};

export const convertVoiceToText = (props: Hook) => {
  const url = generateApi(["globalServices", "convertVoiceToText"]);

  return fetchMiddleWare({
    beforeFetch(ctx) {
      return generatePayload(ctx, {
        attachment_code: "attachmentCode",
        algorithm_name: "algorithmName",
        optimize_audio: "optimizeAudio",
      });
    },
    afterFetch({ data }) {
      return {
        data: {
          message: pathOr("", ["configs", "message"], data),
        },
      };
    },
    ...props,
    url,
    method: "POST",
  });
};

export const getParvandehReportList = (props: Hook) => {
  const url = generateApi(["globalServices", "parvandeh_report"]);
  return fetchMiddleWare({
    afterFetch(response) {
      const items = pathOr([], ["data", "data"], response);
      const permissions = pathOr([], ["configs", "permissions"], response);
      return {
        data: {
          items,
          permissions,
        },
      };
    },
    onFetchError() {},
    ...props,
    url,
    method: "GET",
  });
};

export const getParvandehStatusDetail = (props: Hook) => {
  const url = generateApi(["globalServices", "parvandeh_status_detail"]);

  return fetchMiddleWare({
    async afterFetch({ data }) {
      const totalItemCount = pathOr(0, ["total_item"], data);
      const totalIncompleteItemCount = pathOr(
        0,
        ["total_incomplete_item"],
        data
      );

      const items: any = pathOr([], ["tabs", "0", "items"], data).map(
        (item: any) => {
          return {
            tabName: pathOr("", ["tab_name"], item),
            icon: pathOr("", ["icon"], item),
            caption: pathOr("", ["tab_caption"], item),
            time: pathOr("", ["duration_time"], item),
            done: pathOr(false, ["complete_status"], item),
            link: pathOr(null, ["link"], item),
            hasData: has("has_data", item) ? item?.has_data : "",
            subItems: pathOr([], ["message"], item),
          };
        }
      );
      const allData = {
        totalItemCount,
        totalIncompleteItemCount,
        items,
      };
      updateParvandehStatusDetail(allData);
      return { data: allData };
    },
    ...props,
    url,
    method: "GET",
  });
};

export const getMyParvandeh = (props: Hook) => {
  const url = generateApi(["globalServices", "myParvandeh"]);

  return fetchMiddleWare({
    async afterFetch({ data }) {
      return {
        data: {
          items: data,
        },
      };
    },

    onFetchError() {
      return {
        data: false,
      };
    },
    ...props,
    url,
    method: "GET",
  });
};

export const getMenu = (props?: Hook) => {
  const url = generateApi(["globalServices", "menu"]);

  return fetchMiddleWare({
    async beforeFetch() {
      await storeDispatcher("menuItems", []);
    },
    async afterFetch({ data }) {
      const menuItems = pathOr([], ["result", "data"], data);
      await storeDispatcher("menuItems", menuItems);
    },

    onFetchError() {
      return {
        data: false,
      };
    },
    ...props,
    url,
    method: "GET",
  });
};

export const getUserInfo = (props?: Hook) => {
  const url = generateApi(["globalServices", "user_info"]);

  return fetchMiddleWare({
    async afterFetch({ data }) {
      const announcementInfo = pathOr(null, ["announcement_info"], data);
      const count = pathOr(0, ["count"], announcementInfo);
      const status = pathOr(false, ["status"], announcementInfo);
      storeDispatcher("myAnnouncementsBoxVisible", status);
      storeDispatcher("myAnnouncementsUnreadCount", count);
      await storeDispatcher("userInfo", data);

      const messengersInfo = pathOr([], ["messengers_info"], data);
      await storeDispatcher("messengersInfo", messengersInfo);
    },

    onFetchError() {
      return {
        data: false,
      };
    },
    ...props,
    url,
    method: "GET",
  });
};

export const parvandehChangeRole = (props: Hook) => {
  const url = generateApi(["globalServices", "parvandehChangeRole"]);

  const { mutate: runGetParvandehStatusDetail } = getParvandehStatusDetail({
    immediate: false,
  });

  return fetchMiddleWare({
    beforeFetch(ctx) {
      return generatePayload(ctx, { code: "code" });
    },
    async afterFetch({ data }) {
      const defaultRoute = pathOr(
        "dashboard",
        ["configs", "other", "default_dashboard"],
        data
      ) as string;
      const profileRequiredTabs = pathOr(
        [],
        ["configs", "other", "profile_required_tab"],
        data
      );
      const activeParvandehCode = pathOr(
        null,
        ["configs", "other", "active_parvandeh_code"],
        data
      );

      await persistActiveParvandehDefaultRoute(
        defaultRoute,
        profileRequiredTabs
      );

      const {
        data: { items },
      } = await runGetParvandehStatusDetail({
        replacement: [activeParvandehCode],
      });

      const hasIncompleteRequiredTabs = shouldRedirectToProfileRequiredTabs(
        profileRequiredTabs,
        items
      );

      storeDispatcher("showProfileRequiredTabs", hasIncompleteRequiredTabs);

      let targetRoute =
        defaultRoute === "profile_info" ? "profile" : defaultRoute;
      targetRoute = hasIncompleteRequiredTabs
        ? "profile-required-tabs"
        : targetRoute;

      const message = pathOr("", ["configs", "message"], data);
      return {
        data: {
          message,
          targetRoute,
        },
      };
    },

    ...props,
    url,
    method: "PUT",
  });
};

export const exportExcel = (props: Hook) => {
  const url = generateApi(["globalServices", "exportExcel"]);
  const { toastAlert } = useHooks();
  return fetchMiddleWare({
    afterFetch({ data }) {
      const message = pathOr("", ["configs", "message"], data);
      toastAlert(message);
    },
    beforeFetch(ctx) {
      return generatePayload(ctx, {
        filters: "filters",
        name: "name",
      });
    },

    ...props,
    url,
    method: "PATCH",
  });
};

export const getListWrapper = (props?: Hook) => {
  const url = "";

  return fetchMiddleWare({
    async afterFetch({ data }) {
      await storeDispatcher("mobileHeaderTitleCount", data.total);
    },

    onFetchError() {
      return {
        data: false,
      };
    },
    ...props,
    url,
    method: "GET",
    immediate: false,
  });
};

export function getListWrapperNew<
  TData = any,
  TError = any,
  TVariables extends Record<string, any> = Record<string, any>
>({
  options = {},
}: {
  props?: PropsInterface;
  options?: MutationOptions<TData, TError, TVariables, unknown>;
}) {
  const url = "";
  return fetchMiddlewareQuery({
    url,
    method: "get",
    options: {
      onMutate() {
        const abortController = new AbortController();
        return { abortController };
      },
      ...options,
    },
  });
}

export const getCaptcha = (props?: Hook) => {
  const url = generateApi(["globalServices", "captcha"]);

  return fetchMiddleWare({
    async beforeFetch() {
      await storeDispatcher("menuItems", []);
    },
    async afterFetch({ data }) {
      const mimeType = pathOr(null, ["mime_type"], data);
      const base64Src = pathOr(null, ["captcha"], data);
      const resCaptchaImage = `data:${mimeType};base64,${base64Src}`;
      const resCaptchaKey = pathOr(null, ["key"], data);
      const resExpireSeconds = pathOr(null, ["expire_seconds"], data);
      const resShow = pathOr(false, ["show_captcha"], data);

      return {
        data: {
          resCaptchaImage,
          resCaptchaKey,
          resExpireSeconds,
          resShow,
        },
      };
    },

    ...props,
    url,
    method: "GET",
    immediate: false,
  });
};

export const getEnumAll = (props?: Hook) => {
  const url = generateApi(["globalServices", "enumAll"]);

  return fetchMiddleWare({
    async afterFetch({ data }) {
      const enumArr = pathOr([], ["data"], data);
      await asyncLocalStorage.setItem("enumerations", JSON.stringify(enumArr));
      if (props?.shouldReload) {
        window.location.reload();
      }

      return {
        data: { ...data },
      };
    },

    ...props,
    url,
    method: "GET",
    immediate: false,
  });
};

export function getAttachmentOriginalNew<
  TData = any,
  TError = any,
  TVariables extends Record<string, any> = Record<string, any>
>({
  options,
}: {
  props: PropsInterface;
  options?: MutationOptions<TData, TError, TVariables, unknown>;
}) {
  const url = generateApi(["globalServices", "attachmentOriginal"]);

  return fetchMiddlewareQuery({
    url,
    method: "get",
    transformResponse(data) {
      const imageAddress = data ? URL.createObjectURL(data) : "";
      return imageAddress;
    },
    axiosOptions: {
      responseType: "blob",
    },
    options: {
      ...options,
    },
  });
}

export const getAttachmentOriginal = (props?: Hook) => {
  const url = generateApi(["globalServices", "attachmentOriginal"]);

  return fetchMiddleWare({
    afterFetch({ data }) {
      const imageAddress = data ? URL.createObjectURL(data) : "";
      return { data: { imageAddress } };
    },

    ...props,
    url,
    method: "GET",
    immediate: false,
    responseType: "blob",
  });
};

export const getParvandehCancelPreviewPdf = (props: Hook) => {
  const url = generateApi(["globalServices", "parvandehCancelPreviewPdf"]);

  return fetchMiddleWare({
    beforeFetch(ctx) {
      return generatePayload(ctx, {
        captcha: "captcha",
        key: "key",
      });
    },
    afterFetch({ data }) {
      if (data) {
        downloadBlob(data);
      }
    },
    ...props,
    url,
    method: "PATCH",
    immediate: false,
    responseType: "blob",
  });
};

export const checkAftaAuthentication = (props?: Hook) => {
  const url = generateApi(["globalServices", "checkAftaAuthentication"]);

  return fetchMiddleWare({
    async afterFetch({ data }) {
      return {
        data: {
          isAuthorized: pathOr(false, ["auth_verify"], data),
          hasEmail: pathOr(null, ["has_email"], data),
          hasMobile: pathOr(null, ["has_mobile"], data),
          isActiveEmail: pathOr(null, ["is_active_email"], data),
          isActiveSms: pathOr(null, ["is_active_sms"], data),
        },
      };
    },

    ...props,
    url,
    method: "GET",
    immediate: false,
  });
};

export const authVerifyByPassword = (props: Hook) => {
  const url = generateApi(["globalServices", "authVerifyByPassword"]);
  return fetchMiddleWare({
    beforeFetch(ctx) {
      return generatePayload(ctx, {
        password: "password",
        captcha: "captcha",
        key: "key",
      });
    },
    afterFetch({ data }) {
      return {
        data: {
          message: pathOr(null, ["configs", "message"], data),
        },
      };
    },
    ...props,
    url,
    method: "POST",
  });
};

export const generateMobileOtpForMe = (props: Hook) => {
  const url = generateApi(["globalServices", "generateMobileOtpForMe"]);
  return fetchMiddleWare({
    afterFetch({ data }) {
      return {
        data: {
          controlCode: pathOr(null, ["control_code"], data),
          expireAt: pathOr(null, ["expire_at"], data),
          expireSeconds: pathOr(null, ["expire_seconds"], data),
          mobile: pathOr(null, ["mobile"], data),
          message: pathOr(null, ["configs", "message"], data),
        },
      };
    },
    ...props,
    url,
    method: "GET",
  });
};

export const authVerifyMeByMobile = (props: Hook) => {
  const url = generateApi(["globalServices", "authVerifyMeByMobile"]);
  return fetchMiddleWare({
    beforeFetch(ctx) {
      return generatePayload(ctx, {
        two_step_code: "otpCode",
        control_code: "controlCode",
      });
    },
    afterFetch({ data }) {
      return {
        data: {
          message: pathOr(null, ["configs", "message"], data),
        },
      };
    },
    ...props,
    url,
    method: "POST",
  });
};

export const generateEmailOtpForMe = (props: Hook) => {
  const url = generateApi(["globalServices", "generateEmailOtpForMe"]);
  return fetchMiddleWare({
    afterFetch({ data }) {
      return {
        data: {
          controlCode: pathOr(null, ["control_code"], data),
          expireAt: pathOr(null, ["expire_at"], data),
          expireSeconds: pathOr(null, ["expire_seconds"], data),
          email: pathOr(null, ["email"], data),
          message: pathOr(null, ["configs", "message"], data),
        },
      };
    },
    ...props,
    url,
    method: "GET",
  });
};

export const authVerifyMeByEmail = (props: Hook) => {
  const url = generateApi(["globalServices", "authVerifyMeByEmail"]);
  return fetchMiddleWare({
    beforeFetch(ctx) {
      return generatePayload(ctx, {
        two_step_code: "otpCode",
        control_code: "controlCode",
      });
    },
    afterFetch({ data }) {
      return {
        data: {
          message: pathOr(null, ["configs", "message"], data),
        },
      };
    },
    ...props,
    url,
    method: "POST",
  });
};

export const updateTag = (props: Hook) => {
  const url = generateApi(["resources", "tag"]);

  return fetchMiddleWare({
    beforeFetch(ctx) {
      return generatePayload(ctx, {
        resource: "resourceName",
        key: "resourceValue",
        tag_id: "tags",
      });
    },
    afterFetch({ data }) {
      return {
        data: {
          message: pathOr(null, ["configs", "message"], data),
        },
      };
    },
    ...props,
    url,
    method: "PUT",
  });
};

export const generatePassword = (props: Hook) => {
  const url = generateApi(["globalServices", "generatePassword"]);
  return fetchMiddleWare({
    afterFetch({ data }) {
      return {
        data: {
          password: pathOr(null, ["password"], data),
        },
      };
    },
    ...props,
    url,
    method: "GET",
  });
};

export const createEnum = (props: Hook) => {
  const url = generateApi(["resources", "enum"]);

  const { mutate: runGetEnumAll } = getEnumAll();

  return fetchMiddleWare({
    beforeFetch(ctx) {
      return generatePayload(ctx, {
        enum_caption: "enumCaption",
        category_name: "categoryName",
        parent_id: "parentId",
      });
    },
    async afterFetch({ data }) {
      await runGetEnumAll({});
      return {
        data: { id: data?.id },
      };
    },
    ...props,
    url,
    method: "POST",
  });
};

export const attachmentTus = (props: Hook) => {
  const url = generateApi(["resources", "enum"]);

  const { mutate: runGetEnumAll } = getEnumAll();

  return fetchMiddleWare({
    beforeFetch(ctx) {
      return generatePayload(ctx, {
        enum_caption: "enumCaption",
        category_name: "categoryName",
        parent_id: "parentId",
      });
    },
    async afterFetch({ data }) {
      await runGetEnumAll({});
      return {
        data: { id: data?.id },
      };
    },
    ...props,
    url,
    method: "POST",
  });
};

export const removeMySessionHistory = (props: Hook) => {
  const url = generateApi(["globalServices", "mySessionHistoryDelete"]);

  return fetchMiddleWare({
    afterFetch({ data }) {
      return {
        data: {
          message: pathOr("", ["configs", "message"], data),
        },
      };
    },
    ...props,
    url,
    method: "PATCH",
  });
};

export const getAzmoonExecuteExamPacks = (props: Hook) => {
  const url = generateApi(["globalServices", "azmoonExecuteExamPacks"]);
  return fetchMiddleWare({
    afterFetch({ data }) {
      const dataItems = pathOr([], ["data"], data);
      return {
        data: dataItems.map((item) => {
          return {
            name: pathOr("", ["exam_pack", "name"], item),
            address: pathOr("", ["address"], item),
          };
        }),
      };
    },
    ...props,
    url,
    method: "GET",
  });
};

export const getAttachmentDownload = (props: Hook) => {
  const url = generateApi(["globalServices", "attachmentDownload2"]);
  return fetchMiddleWare({
    afterFetch({ data }) {
      if (data) {
        downloadBlob(data);
      }
    },
    ...props,
    url,
    method: "GET",
    immediate: false,
    responseType: "blob",
  });
};

export const getAttachmentDownload2 = (props: Hook) => {
  const url = generateApi(["globalServices", "attachmentDownload2"]);
  return fetchMiddleWare({
    afterFetch({ data }) {
      if (data) {
        return data;
      }
    },
    ...props,
    url,
    method: "GET",
    immediate: false,
    responseType: "blob",
  });
};

export const getAttachmentBlob = (props: Hook) => {
  const url = generateApi(["globalServices", "attachmentDownload"]);
  return fetchMiddleWare({
    afterFetch({ data }) {
      if (data) {
        return data;
      }
    },
    ...props,
    url,
    method: "GET",
    immediate: false,
    responseType: "blob",
  });
};

const MAX_CACHE_ITEMS = 200;

const manageCacheSize = (queryClient: any) => {
  const queries = queryClient
    .getQueryCache()
    .findAll({ queryKey: ["image-blob"] });

  if (queries.length > MAX_CACHE_ITEMS) {
    const excessQueries = queries.slice(0, queries.length - MAX_CACHE_ITEMS);

    excessQueries.forEach((query: any) => {
      const cachedBlob = queryClient.getQueryData(query.queryKey);
      if (cachedBlob) {
        URL.revokeObjectURL(URL.createObjectURL(cachedBlob));
      }
    });

    excessQueries.forEach((query: any) => {
      queryClient.removeQueries(query.queryKey);
    });
  }
};

export function getAttachmentBlobNew<
  TData = any,
  TError = any,
  TVariables extends Record<string, any> = Record<string, any>
>({
  options,
}: {
  props: PropsInterface;
  options?: MutationOptions<TData, TError, TVariables, unknown>;
}) {
  const url = generateApi(["globalServices", "attachmentDownload"]);

  const queryClient = useQueryClient();
  return fetchMiddlewareQuery({
    url,
    method: "get",
    middlewareOptions: { showError: false },
    axiosOptions: {
      responseType: "blob",
    },
    options: {
      onSuccess(blob, variables) {
        const { replacement } = variables;
        const cacheKey = ["image-blob", replacement[0]];
        queryClient.setQueryData(cacheKey, blob);
        queryClient.setQueryDefaults(cacheKey, {
          cacheTime: 1000 * 60 * 60,
        });
        manageCacheSize(queryClient);
      },
      ...options,
    },
  });
}

export const getAttachmentDownloadSrc = (props: Hook) => {
  const url = generateApi(["globalServices", "attachmentDownload"]);
  return fetchMiddleWare({
    afterFetch({ data }) {
      const imageAddress = data ? URL.createObjectURL(data) : "";
      return { data: { imageAddress } };
    },
    ...props,
    url,
    method: "GET",
    immediate: false,
    responseType: "blob",
  });
};

export const updateReportSetting = (props: Hook) => {
  const url = generateApi(["globalServices", "reportSetting"]);

  return fetchMiddleWare({
    beforeFetch(ctx) {
      return generatePayload(ctx, {
        settings: "settings",
      });
    },
    ...props,
    url,
    method: "PUT",
  });
};

export const getActivity = (props: Hook) => {
  const url = generateApi(["globalServices", "activity"]);
  return fetchMiddleWare({
    afterFetch({ data, configs: { permissions: permissionsData } }) {
      const getData = {
        id: pathOr(null, ["id"], data),
        title: pathOr(null, ["caption"], data),
        description: pathOr(null, ["description"], data),
        stateDefine: pathOr(null, ["term_state_code"], data),
        pagesReferenceObj: pathOr(null, ["action"], data),
        pagesReference: pathOr(null, ["action", "id"], data),
        timeCheckpoint: pathOr(0, ["time_checkpoint"], data),
        viewTime: pathOr(0, ["diff_time_checkpoint"], data),
        durationActivity: pathOr(null, ["duration"], data),
        startAtTime: pathOr(0, ["start_at_time"], data),
        startAtPeriod: pathOr(0, ["start_at_period"], data),
        previousStepRequired: pathOr(0, ["has_prerequisite"], data),
        guideText: pathOr("", ["guide_text"], data),
        isActivityEditable: !pathOr(false, ["is_activity_editable"], data),
        comparisonCondition: "", // needed for use in set values by loop in component
      };

      return {
        data: {
          getData,
          permissionsData,
        },
      };
    },
    ...props,
    url,
    method: "GET",
    immediate: false,
  });
};

export const addActivity = (props: Hook) => {
  const url = generateApi(["globalServices", "activities"]);

  return fetchMiddleWare({
    beforeFetch(ctx) {
      return generatePayload(ctx, {
        term_id: "termId",
        caption: "title",
        description: "description",
        term_state_code: "stateDefine",
        custom_action_id: "pagesReference",
        // time_checkpoint: "timeCheckpoint",
        diff_time_checkpoint: "diffTimeCheckpoint",
        duration: "durationActivity",
        start_at_time: "startAtTime",
        start_at_period: "startAtPeriod",
        has_prerequisite: "previousStepRequired",
        files: "files",
      });
    },
    ...props,
    url,
    method: "POST",
  });
};

export const updateActivity = (props: Hook) => {
  const url = generateApi(["globalServices", "activity"]);

  return fetchMiddleWare({
    beforeFetch(ctx) {
      return generatePayload(ctx, {
        term_id: "termId",
        caption: "title",
        description: "description",
        term_state_code: "stateDefine",
        custom_action_id: "pagesReference",
        // time_checkpoint: "timeCheckpoint",
        diff_time_checkpoint: "diffTimeCheckpoint",
        duration: "durationActivity",
        start_at_time: "startAtTime",
        start_at_period: "startAtPeriod",
        has_prerequisite: "previousStepRequired",
        files: "files",
      });
    },
    ...props,
    url,
    method: "PUT",
  });
};

export const deleteActivity = (props: Hook) => {
  const url = generateApi(["globalServices", "activity"]);
  return fetchMiddleWare({
    ...props,
    url,
    method: "DELETE",
  });
};

export const getMyLastLoginHistoryRecord = (props?: Hook) => {
  const url = generateApi(["profile", "myLastLoginHistory"], [], {
    per_page: 1,
  });

  return fetchMiddleWare({
    async afterFetch({ data }) {
      const items = pathOr([], ["result"], data);
      const lastLoginData = pathOr(null, [items.length > 1 ? "1" : "0"], items);
      return {
        data: {
          ip: pathOr("", ["ip"], lastLoginData),
          device: pathOr("", ["device"], lastLoginData),
          createdAt: pathOr("", ["created_at", "jalali"], lastLoginData),
        },
      };
    },
    ...props,
    url,
    method: "GET",
    immediate: false,
  });
};

export const updateOrder = (props?: Hook) => {
  const url = generateApi(props?.urlKeys);

  return fetchMiddleWare({
    beforeFetch(ctx) {
      return generatePayload(ctx, {
        group: "group",
        start_order: "startOrder",
        end_order: "endOrder",
      });
    },
    afterFetch({ data }) {
      const message = pathOr("", ["configs", "message"], data);
      return { data: { message } };
    },
    ...props,
    url,
    method: "PATCH",
    immediate: false,
  });
};

export function frontErrorLog<
  TData = any,
  TError = any,
  TVariables extends Record<string, any> = Record<string, any>
>({
  options,
}: {
  props: PropsInterface;
  options?: MutationOptions<TData, TError, TVariables, unknown>;
}) {
  const url = generateApi(["globalServices", "frontErrorLog"]);
  return fetchMiddlewareQuery({
    url,
    method: "post",
    mapping: {
      error_caption: "errorCaption",
      error_description: "errorDescription",
      browser: "browser",
      device: "device",
    },
    options: {
      ...options,
    },
  });
}

export function getAudioPeaks<
  TData = any,
  TError = any,
  TVariables extends Record<string, any> = Record<string, any>
>({
  options,
}: {
  props: PropsInterface;
  options?: MutationOptions<TData, TError, TVariables, unknown>;
}) {
  const url = generateApi(["globalServices", "peaks"]);
  return fetchMiddlewareQuery({
    url,
    method: "get",
    options: {
      ...options,
    },
  });
}

type ArgumentsType<TData, TError, TVariables extends Record<string, any>> = {
  props?: PropsInterface;
  options?: Partial<MutationOptions<TData, TError, TVariables, unknown>>;
};

export const voiceToTextOnline = <
  TData = any,
  TError = any,
  TVariables extends Record<string, any> = Record<string, any>
>({
  options,
}: ArgumentsType<TData, TError, TVariables>) => {
  const url = generateApi(["globalServices", "voiceToTextOnline"]);
  return fetchMiddlewareQuery({
    url,
    method: "post",
    mapping: {
      files: "files",
    },
    transformResponse: ({ data }) => {
      const message = pathOr("", ["configs", "message"], data);
      const text = pathOr("", ["text"], data);
      return { message, text };
    },
    options: {
      ...options,
    },
  });
};
